import type {
  CalendarReconcileColumns,
  CalendarReconcileSnapshot,
} from '@blocksuite/affine/data-view';

import type { ViewBackedAppKind } from '../streamlain/view-backed-app/types';

export type OfflineMutationStatus = 'pending' | 'flushing' | 'failed';

export type ViewBackedAppAttachSourceOperation = `${string}.attachSource`;
export type ViewBackedAppCreateSourceOperation = `${string}.createSource`;
export type ViewBackedAppUpdateSourcePreferenceOperation =
  `${string}.updateSourcePreference`;
export type ViewBackedAppSourceMutationOperation =
  | ViewBackedAppAttachSourceOperation
  | ViewBackedAppCreateSourceOperation
  | ViewBackedAppUpdateSourcePreferenceOperation;

export type OfflineMutationOperation =
  | 'blob.upload'
  | 'calendar.attachSource'
  | 'calendar.createMailboxLink'
  | 'calendar.createSource'
  | 'calendar.deleteMailboxLink'
  | 'calendar.reconcileEvent'
  | 'calendar.respondInvitation'
  | 'calendar.updateMailboxLink'
  | 'calendar.updateSourcePreference'
  | ViewBackedAppSourceMutationOperation
  | 'folder.create'
  | 'folder.rename'
  | 'folder.move'
  | 'folder.addDoc'
  | 'folder.addDocToRoot'
  | 'folder.removeDoc'
  | 'permission.grant'
  | 'permission.update'
  | 'permission.revoke'
  | 'mail.updateMessageState'
  | 'mail.moveMessages'
  | 'mail.createFolder'
  | 'mail.updateFolder'
  | 'mail.deleteFolder'
  | 'mail.send';

export interface PermissionGraphMutationMetadata {
  affectedFolderIds?: string[];
  affectedDocIds?: string[];
  expectedPermissionGraphVersion?: number;
  optimisticPatchId?: string;
  rollback?: {
    folderIds?: string[];
    docIds?: string[];
  };
}

export interface FolderCreateMutationPayload extends PermissionGraphMutationMetadata {
  folderId: string;
  name: string;
  parentId: string | null;
  visibility?: string;
  inheritPermissions?: boolean;
}

export interface FolderRenameMutationPayload extends PermissionGraphMutationMetadata {
  folderId: string;
  name: string;
}

export interface FolderMoveMutationPayload extends PermissionGraphMutationMetadata {
  folderId: string;
  parentId: string | null;
}

export interface FolderAddDocMutationPayload extends PermissionGraphMutationMetadata {
  folderId: string;
  docId: string;
}

export interface FolderAddDocToRootMutationPayload extends PermissionGraphMutationMetadata {
  docId: string;
}

export interface FolderRemoveDocMutationPayload extends PermissionGraphMutationMetadata {
  docId: string;
}

export type ObjectPermissionTargetKind = 'doc' | 'folder';
export type ObjectPermissionPrincipalKind = 'group' | 'user';

export interface ObjectPermissionTargetRef {
  id: string;
  kind: ObjectPermissionTargetKind;
}

export interface ObjectPermissionPrincipalRef {
  id: string;
  kind: ObjectPermissionPrincipalKind;
}

export interface PermissionMutationPayload extends PermissionGraphMutationMetadata {
  principal: ObjectPermissionPrincipalRef;
  role?: string;
  target: ObjectPermissionTargetRef;
}

export type PermissionGrantMutationPayload = PermissionMutationPayload & {
  role: string;
};

export type PermissionUpdateMutationPayload = PermissionMutationPayload & {
  role: string;
};

export type PermissionRevokeMutationPayload = PermissionMutationPayload;

export interface MailUpdateMessageStateMutationPayload {
  input: {
    workspaceId: string;
    messageId: string;
    isRead?: boolean | null;
    isFlagged?: boolean | null;
    isStarred?: boolean | null;
  };
}

export interface MailMoveMessagesMutationPayload {
  workspaceId: string;
  messageIds: string[];
  folderId: string;
}

export interface MailCreateFolderMutationPayload {
  input: {
    workspaceId: string;
    accountId: string;
    displayName: string;
    parentFolderId?: string | null;
    path?: string | null;
    parentPath?: string | null;
  };
}

export interface MailUpdateFolderMutationPayload {
  input: {
    workspaceId: string;
    folderId: string;
    displayName?: string | null;
  };
}

export interface MailDeleteFolderMutationPayload {
  workspaceId: string;
  folderId: string;
}

export interface MailSendMutationPayload {
  input: {
    workspaceId: string;
    accountId: string;
    idempotencyKey: string;
    composeKind?: string | null;
    sourceMessageId?: string | null;
    inReplyToHeader?: string | null;
    referencesHeader?: string | null;
    signatureId?: string | null;
    to: string[];
    cc?: string[] | null;
    bcc?: string[] | null;
    subject?: string | null;
    bodyText?: string | null;
    bodyHtml?: string | null;
    attachments?: Array<{
      blobKey: string;
      filename: string;
      mimeType?: string | null;
    }> | null;
  };
}

export interface BlobUploadMutationPayload {
  workspaceId: string;
  blobKey: string;
  docId?: string;
  mimeType: string;
  size: number;
}

export interface ViewBackedAppSourceRefMutationPayload {
  appKind: ViewBackedAppKind;
  blockId: string;
  databaseId: string;
  docId: string;
  viewId: string;
  viewMode: string;
}

export interface ViewBackedAppAttachSourceMutationPayload {
  areaRootFolderId?: string | null;
  blockId: string;
  databaseId: string;
  docId: string;
  title?: string | null;
  viewId: string;
}

export type ViewBackedAppCreateSourceMutationPayload =
  ViewBackedAppAttachSourceMutationPayload;

export interface ViewBackedAppUpdateSourcePreferenceMutationPayload {
  patch: {
    colorOverride?: string | null;
    titleOverride?: string | null;
    visible?: boolean;
  };
  sourceId: string;
}

export type CalendarSourceRefMutationPayload =
  ViewBackedAppSourceRefMutationPayload;

export type CalendarAttachSourceMutationPayload =
  ViewBackedAppAttachSourceMutationPayload;

export type CalendarCreateSourceMutationPayload =
  ViewBackedAppCreateSourceMutationPayload;

export type CalendarUpdateSourcePreferenceMutationPayload =
  ViewBackedAppUpdateSourcePreferenceMutationPayload;

export interface CalendarCreateMailboxLinkMutationPayload {
  input: {
    inboundEnabled: boolean;
    mailAccountId: string;
    outboundEnabled: boolean;
  };
  source: CalendarSourceRefMutationPayload;
}

export interface CalendarUpdateMailboxLinkMutationPayload {
  linkId: string;
  patch: {
    inboundEnabled?: boolean;
    outboundEnabled?: boolean;
    status?: 'active' | 'disabled';
  };
  source: CalendarSourceRefMutationPayload;
}

export interface CalendarDeleteMailboxLinkMutationPayload {
  linkId: string;
  source: CalendarSourceRefMutationPayload;
}

export interface CalendarRespondInvitationMutationPayload {
  icsSequence?: number | null;
  requestId: string;
  rowId: string;
  source: CalendarSourceRefMutationPayload;
  status: 'accepted' | 'declined' | 'tentative';
}

export interface CalendarReconcileEventMutationPayload {
  columns: CalendarReconcileColumns;
  databaseId: string;
  docId: string;
  previousEvent: CalendarReconcileSnapshot | null;
  requestId: string;
  rowId: string;
  skipMail: boolean;
  syncDocIds: string[];
  viewId: string;
}

export type OfflineMutationPayloadByOperation = {
  'blob.upload': BlobUploadMutationPayload;
  'calendar.attachSource': ViewBackedAppAttachSourceMutationPayload;
  'calendar.createMailboxLink': CalendarCreateMailboxLinkMutationPayload;
  'calendar.createSource': ViewBackedAppCreateSourceMutationPayload;
  'calendar.deleteMailboxLink': CalendarDeleteMailboxLinkMutationPayload;
  'calendar.reconcileEvent': CalendarReconcileEventMutationPayload;
  'calendar.respondInvitation': CalendarRespondInvitationMutationPayload;
  'calendar.updateMailboxLink': CalendarUpdateMailboxLinkMutationPayload;
  'calendar.updateSourcePreference': ViewBackedAppUpdateSourcePreferenceMutationPayload;
  'folder.create': FolderCreateMutationPayload;
  'folder.rename': FolderRenameMutationPayload;
  'folder.move': FolderMoveMutationPayload;
  'folder.addDoc': FolderAddDocMutationPayload;
  'folder.addDocToRoot': FolderAddDocToRootMutationPayload;
  'folder.removeDoc': FolderRemoveDocMutationPayload;
  'permission.grant': PermissionGrantMutationPayload;
  'permission.update': PermissionUpdateMutationPayload;
  'permission.revoke': PermissionRevokeMutationPayload;
  'mail.updateMessageState': MailUpdateMessageStateMutationPayload;
  'mail.moveMessages': MailMoveMessagesMutationPayload;
  'mail.createFolder': MailCreateFolderMutationPayload;
  'mail.updateFolder': MailUpdateFolderMutationPayload;
  'mail.deleteFolder': MailDeleteFolderMutationPayload;
  'mail.send': MailSendMutationPayload;
} & {
  [K in ViewBackedAppAttachSourceOperation]: ViewBackedAppAttachSourceMutationPayload;
} & {
  [K in ViewBackedAppCreateSourceOperation]: ViewBackedAppCreateSourceMutationPayload;
} & {
  [K in ViewBackedAppUpdateSourcePreferenceOperation]: ViewBackedAppUpdateSourcePreferenceMutationPayload;
};

export type OfflineMutationPayload<
  Operation extends OfflineMutationOperation = OfflineMutationOperation,
> = OfflineMutationPayloadByOperation[Operation];

export interface OfflineMutationScope {
  workspaceId: string;
  cacheOwnerKey: string;
}

export interface OfflineMutationRecord<
  Operation extends OfflineMutationOperation = OfflineMutationOperation,
> extends OfflineMutationScope {
  id: string;
  scopeKey: string;
  operation: Operation;
  payload: OfflineMutationPayload<Operation>;
  idempotencyKey: string;
  status: OfflineMutationStatus;
  attempts: number;
  createdAt: number;
  sequence?: number;
  updatedAt: number;
  lastError?: string;
}

export type OfflineMutationInput<
  Operation extends OfflineMutationOperation = OfflineMutationOperation,
> = {
  operation: Operation;
  payload: OfflineMutationPayload<Operation>;
  idempotencyKey: string;
  dedupePending?: boolean;
  id?: string;
};

export type OfflineMutationExecutor<
  Operation extends OfflineMutationOperation = OfflineMutationOperation,
> = (mutation: OfflineMutationRecord<Operation>) => Promise<void>;

export interface OfflineMutationOutboxStorage {
  listPending(scope: OfflineMutationScope): Promise<OfflineMutationRecord[]>;
  listFailed(scope: OfflineMutationScope): Promise<OfflineMutationRecord[]>;
  listAll(scope: OfflineMutationScope): Promise<OfflineMutationRecord[]>;
  findPendingByIdempotencyKey(
    scope: OfflineMutationScope,
    idempotencyKey: string
  ): Promise<OfflineMutationRecord | null>;
  upsert(mutation: OfflineMutationRecord): Promise<void>;
  update(mutation: OfflineMutationRecord): Promise<void>;
  remove(id: string): Promise<void>;
  clearScope(scope: OfflineMutationScope): Promise<void>;
}

export function offlineMutationScopeKey(scope: OfflineMutationScope): string {
  return `${scope.workspaceId}:${scope.cacheOwnerKey}`;
}
